import { useEffect, useMemo, useState } from 'react'
import {
  CalendarDays,
  CheckCircle2,
  Circle,
  Columns3,
  Flag,
  LayoutList,
  Plus,
  Send,
  StickyNote,
  Trash2,
  UserRound,
  X
} from 'lucide-react'
import { supabase } from '../supabase'
import { useToast } from '../Toast'
import { useUser } from '../UserContext'

const STORAGE_KEY = 'delivr_platform_todos_v1'

const EMPTY_TASK = {
  title: '',
  status: 'todo',
  priority: 'medium',
  owner: '',
  due_date: '',
  notes: ''
}

const STATUS_OPTIONS = [
  { value: 'todo', label: 'To do', color: '#64748B', bg: '#F1F5F9' },
  { value: 'in_progress', label: 'In progress', color: '#4F46E5', bg: '#EEF2FF' },
  { value: 'blocked', label: 'Blocked', color: '#B45309', bg: '#FEF3C7' },
  { value: 'done', label: 'Done', color: '#15803D', bg: '#DCFCE7' }
]

const PRIORITY_OPTIONS = [
  { value: 'low', label: 'Low' },
  { value: 'medium', label: 'Medium' },
  { value: 'high', label: 'High' }
]

function statusConfig(status) {
  return STATUS_OPTIONS.find(option => option.value === status) || STATUS_OPTIONS[0]
}

function formatDate(date) {
  if (!date) return 'No date'

  return new Date(date).toLocaleDateString('en-GB', {
    day: 'numeric',
    month: 'short',
    year: 'numeric'
  })
}

function isOverdue(task) {
  if (!task.due_date || task.status === 'done') return false

  const today = new Date()
  today.setHours(0, 0, 0, 0)

  const dueDate = new Date(task.due_date)
  dueDate.setHours(0, 0, 0, 0)

  return dueDate < today
}

function readStoredTasks() {
  try {
    const stored = window.localStorage.getItem(STORAGE_KEY)
    return stored ? JSON.parse(stored) : []
  } catch (error) {
    console.error('Failed to load platform todos:', error)
    return []
  }
}

function getOwnerLabel(admin) {
  return admin.full_name || admin.email
}

export default function PlatformTodos() {
  const toast = useToast()
  const { profile } = useUser()
  const defaultOwner = profile?.full_name || profile?.email || ''
  const [tasks, setTasks] = useState([])
  const [comments, setComments] = useState([])
  const [ownerOptions, setOwnerOptions] = useState([])
  const [loading, setLoading] = useState(true)
  const [form, setForm] = useState({ ...EMPTY_TASK, owner: defaultOwner })
  const [statusFilter, setStatusFilter] = useState('')
  const [priorityFilter, setPriorityFilter] = useState('')
  const [search, setSearch] = useState('')
  const [viewMode, setViewMode] = useState('list')
  const [selectedTaskId, setSelectedTaskId] = useState(null)
  const [onlyMine, setOnlyMine] = useState(false)
  const [showTaskModal, setShowTaskModal] = useState(false)
  const [commentDrafts, setCommentDrafts] = useState({})

  useEffect(() => {
    fetchTasks()
    fetchComments()
    fetchOwnerOptions()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    if (form.owner) return
    setForm(prev => ({ ...prev, owner: defaultOwner }))
  }, [defaultOwner, form.owner])

  async function fetchTasks() {
    setLoading(true)

    const { data, error } = await supabase
      .from('platform_todos')
      .select('*')
      .order('created_at', { ascending: false })

    if (error) {
      toast.error(`Failed to load platform tasks: ${error.message}`)
      setTasks([])
      setLoading(false)
      return
    }

    const remoteTasks = data || []

    if (remoteTasks.length === 0) {
      const localTasks = readStoredTasks()

      if (localTasks.length > 0) {
        const payload = localTasks.map(task => ({
          title: task.title,
          status: task.status || 'todo',
          priority: task.priority || 'medium',
          owner: task.owner || null,
          due_date: task.due_date || null,
          notes: task.notes || null,
          created_by: profile?.id || null
        }))

        const { data: migratedTasks, error: migrationError } = await supabase
          .from('platform_todos')
          .insert(payload)
          .select()

        if (!migrationError) {
          window.localStorage.removeItem(STORAGE_KEY)
          setTasks(migratedTasks || [])
          setLoading(false)
          return
        }

        console.error('Failed to migrate local platform tasks:', migrationError)
      }
    }

    setTasks(remoteTasks)
    setLoading(false)
  }

  async function fetchOwnerOptions() {
    const { data, error } = await supabase
      .from('profiles')
      .select('id, email, full_name')
      .eq('is_platform_admin', true)
      .order('full_name')

    if (error) {
      toast.error(`Failed to load owners: ${error.message}`)
      setOwnerOptions([])
      return
    }

    setOwnerOptions(data || [])
  }

  async function fetchComments() {
    const { data, error } = await supabase
      .from('platform_todo_comments')
      .select('*, profiles(id, email, full_name)')
      .order('created_at', { ascending: true })

    if (error) {
      console.error('Failed to load task comments:', error)
      setComments([])
      return
    }

    setComments(data || [])
  }

  const filteredTasks = useMemo(() => {
    return tasks
      .filter(task => {
        const query = search.toLowerCase()
        const matchesSearch = !query ||
          task.title.toLowerCase().includes(query) ||
          task.notes.toLowerCase().includes(query) ||
          task.owner.toLowerCase().includes(query)
        const matchesStatus = statusFilter ? task.status === statusFilter : true
        const matchesPriority = priorityFilter ? task.priority === priorityFilter : true
        const matchesMine = onlyMine ? task.owner === defaultOwner : true

        return matchesSearch && matchesStatus && matchesPriority && matchesMine
      })
      .sort((a, b) => {
        if (a.status === 'done' && b.status !== 'done') return 1
        if (a.status !== 'done' && b.status === 'done') return -1

        const dateA = a.due_date ? new Date(a.due_date).getTime() : Number.MAX_SAFE_INTEGER
        const dateB = b.due_date ? new Date(b.due_date).getTime() : Number.MAX_SAFE_INTEGER

        return dateA - dateB
      })
  }, [defaultOwner, onlyMine, priorityFilter, search, statusFilter, tasks])

  const taskStats = useMemo(() => {
    return {
      total: tasks.length,
      open: tasks.filter(task => task.status !== 'done').length,
      done: tasks.filter(task => task.status === 'done').length,
      overdue: tasks.filter(isOverdue).length
    }
  }, [tasks])

  const selectedTask = tasks.find(task => task.id === selectedTaskId) || null
  const selectedTaskComments = selectedTask
    ? comments.filter(comment => comment.task_id === selectedTask.id)
    : []

  function openTaskModal(status = 'todo') {
    setForm({
      ...EMPTY_TASK,
      status,
      owner: defaultOwner
    })
    setShowTaskModal(true)
  }

  async function addTask(event) {
    event.preventDefault()

    if (!form.title.trim()) return

    const payload = {
        title: form.title.trim(),
        status: form.status,
        priority: form.priority,
        owner: form.owner || null,
        due_date: form.due_date || null,
        notes: form.notes.trim() || null,
        created_by: profile?.id || null
    }

    const { data, error } = await supabase
      .from('platform_todos')
      .insert(payload)
      .select()
      .single()

    if (error) {
      toast.error(`Failed to add task: ${error.message}`)
      return
    }

    setTasks(prev => [data, ...prev])
    setForm({ ...EMPTY_TASK, owner: defaultOwner })
    setShowTaskModal(false)
  }

  async function updateTask(taskId, updates) {
    const previousTasks = tasks

    setTasks(prev => prev.map(task =>
      task.id === taskId
        ? { ...task, ...updates, updated_at: new Date().toISOString() }
        : task
    ))

    const { error } = await supabase
      .from('platform_todos')
      .update(updates)
      .eq('id', taskId)

    if (error) {
      toast.error(`Failed to update task: ${error.message}`)
      setTasks(previousTasks)
    }
  }

  async function deleteTask(taskId) {
    const previousTasks = tasks

    setTasks(prev => prev.filter(task => task.id !== taskId))

    const { error } = await supabase
      .from('platform_todos')
      .delete()
      .eq('id', taskId)

    if (error) {
      toast.error(`Failed to delete task: ${error.message}`)
      setTasks(previousTasks)
    }
  }

  async function clearCompleted() {
    const previousTasks = tasks

    setTasks(prev => prev.filter(task => task.status !== 'done'))

    const { error } = await supabase
      .from('platform_todos')
      .delete()
      .eq('status', 'done')

    if (error) {
      toast.error(`Failed to clear completed tasks: ${error.message}`)
      setTasks(previousTasks)
    }
  }

  async function addComment(taskId, parentCommentId = null) {
    const draftKey = parentCommentId || taskId
    const body = (commentDrafts[draftKey] || '').trim()

    if (!body) return

    const payload = {
      task_id: taskId,
      parent_comment_id: parentCommentId,
      body,
      user_id: profile?.id || null
    }

    const { data, error } = await supabase
      .from('platform_todo_comments')
      .insert(payload)
      .select('*, profiles(id, email, full_name)')
      .single()

    if (error) {
      toast.error(`Failed to post comment: ${error.message}`)
      return
    }

    setComments(prev => [...prev, data])
    setCommentDrafts(prev => ({ ...prev, [draftKey]: '' }))
  }

  return (
    <div className="hv-page" style={{ maxWidth: '1280px' }}>
      <div className="hv-page-header">
        <div>
          <h1 className="hv-page-title">To-do List</h1>
        </div>

        <button
          className="hv-btn hv-btn-secondary"
          disabled={taskStats.done === 0}
          onClick={clearCompleted}
        >
          Clear Completed
        </button>
      </div>

      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(4, minmax(0, 1fr))',
        gap: 14,
        marginBottom: 20
      }}>
        <StatCard label="Total Tasks" value={taskStats.total} />
        <StatCard label="Open" value={taskStats.open} />
        <StatCard label="Completed" value={taskStats.done} />
        <StatCard label="Overdue" value={taskStats.overdue} tone={taskStats.overdue > 0 ? 'danger' : 'default'} />
      </div>

      <form
        className="hv-card"
        onSubmit={addTask}
        style={{
          padding: 18,
          marginBottom: 20,
          display: 'grid',
          gridTemplateColumns: 'minmax(240px, 1.4fr) 150px 140px 170px 150px auto',
          gap: 10,
          alignItems: 'end'
        }}
      >
        <div>
          <label className="hv-form-label">Task</label>
          <input
            className="hv-input"
            value={form.title}
            onChange={event => setForm({ ...form, title: event.target.value })}
            placeholder="e.g. QA rights import flow"
          />
        </div>

        <div>
          <label className="hv-form-label">Status</label>
          <select
            className="hv-select"
            value={form.status}
            onChange={event => setForm({ ...form, status: event.target.value })}
          >
            {STATUS_OPTIONS.map(option => (
              <option key={option.value} value={option.value}>{option.label}</option>
            ))}
          </select>
        </div>

        <div>
          <label className="hv-form-label">Priority</label>
          <select
            className="hv-select"
            value={form.priority}
            onChange={event => setForm({ ...form, priority: event.target.value })}
          >
            {PRIORITY_OPTIONS.map(option => (
              <option key={option.value} value={option.value}>{option.label}</option>
            ))}
          </select>
        </div>

        <div>
          <label className="hv-form-label">Owner</label>
          <select
            className="hv-select"
            value={form.owner}
            onChange={event => setForm({ ...form, owner: event.target.value })}
          >
            <option value="">Unassigned</option>
            {ownerOptions.map(owner => {
              const label = getOwnerLabel(owner)

              return (
                <option key={owner.id} value={label}>
                  {label}
                </option>
              )
            })}
          </select>
        </div>

        <div>
          <label className="hv-form-label">Due Date</label>
          <input
            className="hv-input"
            type="date"
            value={form.due_date}
            onChange={event => setForm({ ...form, due_date: event.target.value })}
          />
        </div>

        <button className="hv-btn hv-btn-primary" type="submit">
          <Plus size={15} />
          Add Task
        </button>

        <div style={{ gridColumn: '1 / -1' }}>
          <label className="hv-form-label">Notes</label>
          <textarea
            className="hv-input"
            value={form.notes}
            onChange={event => setForm({ ...form, notes: event.target.value })}
            placeholder="Add context, links, decisions or blockers..."
            style={{ minHeight: 70, resize: 'vertical', lineHeight: 1.5 }}
          />
        </div>
      </form>

      <div className="hv-card" style={{ padding: 18 }}>
        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 14,
          marginBottom: 16
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <button
              className={`hv-btn ${viewMode === 'list' ? 'hv-btn-primary' : 'hv-btn-secondary'}`}
              style={{ padding: '8px 11px', fontSize: 12 }}
              onClick={() => setViewMode('list')}
            >
              <LayoutList size={14} />
              List
            </button>

            <button
              className={`hv-btn ${viewMode === 'board' ? 'hv-btn-primary' : 'hv-btn-secondary'}`}
              style={{ padding: '8px 11px', fontSize: 12 }}
              onClick={() => setViewMode('board')}
            >
              <Columns3 size={14} />
              Board
            </button>
          </div>

          <div style={{ display: 'flex', gap: 10 }}>
            <button
              className={`hv-btn ${onlyMine ? 'hv-btn-primary' : 'hv-btn-secondary'}`}
              style={{ padding: '8px 11px', fontSize: 12 }}
              onClick={() => setOnlyMine(prev => !prev)}
            >
              My Tasks
            </button>

            <input
              className="hv-input"
              value={search}
              onChange={event => setSearch(event.target.value)}
              placeholder="Search tasks..."
              style={{ width: 240 }}
            />

            <select
              className="hv-select"
              value={statusFilter}
              onChange={event => setStatusFilter(event.target.value)}
              style={{ width: 150 }}
            >
              <option value="">All statuses</option>
              {STATUS_OPTIONS.map(option => (
                <option key={option.value} value={option.value}>{option.label}</option>
              ))}
            </select>

            <select
              className="hv-select"
              value={priorityFilter}
              onChange={event => setPriorityFilter(event.target.value)}
              style={{ width: 140 }}
            >
              <option value="">All priorities</option>
              {PRIORITY_OPTIONS.map(option => (
                <option key={option.value} value={option.value}>{option.label}</option>
              ))}
            </select>
          </div>
        </div>

        {loading ? (
          <div style={{ color: 'var(--hv-text-muted)', fontSize: 13, padding: '28px 0' }}>
            Loading tasks...
          </div>
        ) : filteredTasks.length === 0 ? (
          <div className="hv-empty">
            <div className="hv-empty-title">No tasks found</div>
            <div className="hv-empty-text">Add a task above or adjust your filters.</div>
          </div>
        ) : viewMode === 'board' ? (
            <BoardView
            tasks={filteredTasks}
            ownerOptions={ownerOptions}
            onUpdate={updateTask}
            onDelete={deleteTask}
            onOpen={task => setSelectedTaskId(task.id)}
            onAddTask={openTaskModal}
          />
        ) : (
          <div style={{ display: 'grid', gap: 10 }}>
            {filteredTasks.map(task => (
              <TaskRow
                key={task.id}
                task={task}
                ownerOptions={ownerOptions}
                onUpdate={updates => updateTask(task.id, updates)}
                onDelete={() => deleteTask(task.id)}
                onOpen={() => setSelectedTaskId(task.id)}
              />
            ))}
          </div>
        )}
      </div>

      {selectedTask && (
        <TaskDetailPanel
          task={selectedTask}
          ownerOptions={ownerOptions}
          onUpdate={updates => updateTask(selectedTask.id, updates)}
          onDelete={() => {
            deleteTask(selectedTask.id)
            setSelectedTaskId(null)
          }}
          comments={selectedTaskComments}
          commentDrafts={commentDrafts}
          setCommentDrafts={setCommentDrafts}
          onAddComment={addComment}
          onClose={() => setSelectedTaskId(null)}
        />
      )}

      {showTaskModal && (
        <TaskModal
          form={form}
          setForm={setForm}
          ownerOptions={ownerOptions}
          onSave={addTask}
          onClose={() => setShowTaskModal(false)}
        />
      )}
    </div>
  )
}

function StatCard({ label, value, tone = 'default' }) {
  return (
    <div className="hv-card" style={{ padding: 18 }}>
      <div style={{
        fontSize: 22,
        fontWeight: 800,
        color: tone === 'danger' ? 'var(--hv-red)' : 'var(--hv-text-primary)',
        marginBottom: 4
      }}>
        {value}
      </div>
      <div style={{ fontSize: 11, fontWeight: 800, color: 'var(--hv-text-muted)', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
        {label}
      </div>
    </div>
  )
}

function BoardView({ tasks, ownerOptions, onUpdate, onDelete, onOpen, onAddTask }) {
  return (
    <div style={{
      display: 'grid',
      gridTemplateColumns: 'repeat(4, minmax(220px, 1fr))',
      gap: 12,
      alignItems: 'start',
      overflowX: 'auto',
      paddingBottom: 4
    }}>
      {STATUS_OPTIONS.map(status => {
        const columnTasks = tasks.filter(task => task.status === status.value)

        return (
          <div
            key={status.value}
            style={{
              background: '#F8FAFC',
              border: '1px solid var(--hv-border)',
              borderRadius: 12,
              minHeight: 240,
              overflow: 'hidden'
            }}
          >
            <div style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              padding: '12px 12px 10px',
              borderTop: `3px solid ${status.color}`
            }}>
              <div style={{ fontSize: 12, fontWeight: 900, color: 'var(--hv-text-primary)' }}>
                {status.label}
              </div>

              <span className="hv-badge hv-badge-gray">{columnTasks.length}</span>
            </div>

            <div style={{ padding: '0 10px 10px' }}>
              <button
                className="hv-btn hv-btn-secondary"
                style={{ width: '100%', justifyContent: 'center', fontSize: 12 }}
                onClick={() => onAddTask(status.value)}
              >
                <Plus size={14} />
                Add Task
              </button>
            </div>

            <div style={{ display: 'grid', gap: 10, padding: 10 }}>
              {columnTasks.length === 0 ? (
                <div style={{
                  border: '1px dashed var(--hv-border)',
                  borderRadius: 10,
                  color: 'var(--hv-text-muted)',
                  fontSize: 12,
                  padding: 16,
                  textAlign: 'center'
                }}>
                  No tasks
                </div>
              ) : columnTasks.map(task => (
                <TaskCard
                  key={task.id}
                  task={task}
                  ownerOptions={ownerOptions}
                  onUpdate={updates => onUpdate(task.id, updates)}
                  onDelete={() => onDelete(task.id)}
                  onOpen={() => onOpen(task)}
                />
              ))}
            </div>
          </div>
        )
      })}
    </div>
  )
}

function TaskCard({ task, ownerOptions, onUpdate, onDelete, onOpen }) {
  const status = statusConfig(task.status)
  const overdue = isOverdue(task)

  return (
    <div
      onClick={onOpen}
      style={{
        background: '#FFFFFF',
        border: '1px solid var(--hv-border)',
        borderRadius: 10,
        padding: 12,
        cursor: 'pointer',
        boxShadow: '0 1px 2px rgba(15, 23, 42, 0.04)'
      }}
    >
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8 }}>
        <button
          className="hv-btn hv-btn-ghost"
          style={{ padding: 2, color: task.status === 'done' ? '#16A34A' : 'var(--hv-text-muted)' }}
          onClick={event => {
            event.stopPropagation()
            onUpdate({ status: task.status === 'done' ? 'todo' : 'done' })
          }}
        >
          {task.status === 'done' ? <CheckCircle2 size={17} /> : <Circle size={17} />}
        </button>

        <div style={{
          flex: 1,
          fontSize: 13,
          fontWeight: 800,
          color: 'var(--hv-text-primary)',
          lineHeight: 1.35,
          textDecoration: task.status === 'done' ? 'line-through' : 'none'
        }}>
          {task.title}
        </div>
      </div>

      {task.notes && (
        <div style={{
          color: 'var(--hv-text-secondary)',
          fontSize: 12,
          lineHeight: 1.45,
          marginTop: 9,
          display: '-webkit-box',
          WebkitLineClamp: 2,
          WebkitBoxOrient: 'vertical',
          overflow: 'hidden'
        }}>
          {task.notes}
        </div>
      )}

      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 7, marginTop: 12 }}>
        <span className="hv-badge hv-badge-gray" style={{ background: status.bg, color: status.color }}>
          {status.label}
        </span>
        <span className="hv-badge hv-badge-gray">
          {task.priority.charAt(0).toUpperCase() + task.priority.slice(1)}
        </span>
        {overdue && <span className="hv-badge hv-badge-red">Overdue</span>}
      </div>

      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        gap: 8,
        marginTop: 12,
        color: 'var(--hv-text-muted)',
        fontSize: 11
      }}>
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, minWidth: 0 }}>
          <UserRound size={12} />
          <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {task.owner || 'Unassigned'}
          </span>
        </span>

        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, color: overdue ? 'var(--hv-red)' : 'var(--hv-text-muted)' }}>
          <CalendarDays size={12} />
          {formatDate(task.due_date)}
        </span>
      </div>

      <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 10 }}>
        <button
          className="hv-btn hv-btn-danger"
          style={{ padding: '5px 7px' }}
          onClick={event => {
            event.stopPropagation()
            onDelete()
          }}
        >
          <Trash2 size={13} />
        </button>
      </div>
    </div>
  )
}

function TaskRow({ task, ownerOptions, onUpdate, onDelete, onOpen }) {
  const status = statusConfig(task.status)
  const overdue = isOverdue(task)

  return (
    <div
      onClick={onOpen}
      style={{
        border: '1px solid var(--hv-border)',
        borderRadius: 10,
        background: task.status === 'done' ? '#FAFBFD' : '#FFFFFF',
        overflow: 'hidden',
        cursor: 'pointer'
      }}
    >
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'minmax(240px, 1.4fr) 150px 130px 170px 140px auto',
          gap: 10,
          alignItems: 'center',
          padding: 12
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, minWidth: 0 }}>
          <button
            className="hv-btn hv-btn-ghost"
            style={{ padding: 4, color: task.status === 'done' ? '#16A34A' : 'var(--hv-text-muted)' }}
            onClick={event => {
              event.stopPropagation()
              onUpdate({ status: task.status === 'done' ? 'todo' : 'done' })
            }}
          >
            {task.status === 'done' ? <CheckCircle2 size={18} /> : <Circle size={18} />}
          </button>

          <input
            className="hv-input"
            value={task.title}
            onClick={event => event.stopPropagation()}
            onChange={event => onUpdate({ title: event.target.value })}
            style={{
              fontWeight: 800,
              textDecoration: task.status === 'done' ? 'line-through' : 'none'
            }}
          />
        </div>

        <select
          className="hv-select"
          value={task.status}
          onClick={event => event.stopPropagation()}
          onChange={event => onUpdate({ status: event.target.value })}
          style={{ color: status.color, background: status.bg, fontWeight: 800 }}
        >
          {STATUS_OPTIONS.map(option => (
            <option key={option.value} value={option.value}>{option.label}</option>
          ))}
        </select>

        <select
          className="hv-select"
          value={task.priority}
          onClick={event => event.stopPropagation()}
          onChange={event => onUpdate({ priority: event.target.value })}
        >
          {PRIORITY_OPTIONS.map(option => (
            <option key={option.value} value={option.value}>{option.label}</option>
          ))}
        </select>

        <div style={{ position: 'relative' }}>
          <UserRound size={13} style={{ position: 'absolute', left: 11, top: 11, color: 'var(--hv-text-muted)' }} />
          <select
            className="hv-select"
            value={task.owner}
            onClick={event => event.stopPropagation()}
            onChange={event => onUpdate({ owner: event.target.value })}
            style={{ paddingLeft: 32 }}
          >
            <option value="">Unassigned</option>
            {task.owner && !ownerOptions.some(owner => getOwnerLabel(owner) === task.owner) && (
              <option value={task.owner}>{task.owner}</option>
            )}
            {ownerOptions.map(owner => {
              const label = getOwnerLabel(owner)

              return (
                <option key={owner.id} value={label}>
                  {label}
                </option>
              )
            })}
          </select>
        </div>

        <div style={{ position: 'relative' }}>
          <CalendarDays size={13} style={{ position: 'absolute', left: 11, top: 11, color: overdue ? 'var(--hv-red)' : 'var(--hv-text-muted)' }} />
          <input
            className="hv-input"
            type="date"
            value={task.due_date}
            onClick={event => event.stopPropagation()}
            onChange={event => onUpdate({ due_date: event.target.value })}
            title={formatDate(task.due_date)}
            style={{
              paddingLeft: 32,
              borderColor: overdue ? '#FCA5A5' : 'var(--hv-border)'
            }}
          />
        </div>

        <button
          className="hv-btn hv-btn-danger"
          style={{ padding: 8 }}
          onClick={event => {
            event.stopPropagation()
            onDelete()
          }}
        >
          <Trash2 size={15} />
        </button>
      </div>

      <div style={{
        display: 'grid',
        gridTemplateColumns: '130px minmax(0, 1fr)',
        gap: 10,
        padding: '0 12px 12px 56px',
        alignItems: 'start'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 7, color: 'var(--hv-text-muted)', fontSize: 12, fontWeight: 800 }}>
          <StickyNote size={13} />
          Notes
        </div>

        <textarea
          className="hv-input"
          value={task.notes}
          onClick={event => event.stopPropagation()}
          onChange={event => onUpdate({ notes: event.target.value })}
          placeholder="Add notes..."
          style={{ minHeight: 58, resize: 'vertical', lineHeight: 1.5 }}
        />
      </div>

      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        gap: 10,
        borderTop: '1px solid var(--hv-border-light)',
        padding: '8px 12px 8px 56px',
        fontSize: 11,
        color: 'var(--hv-text-muted)'
      }}>
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
          <Flag size={12} />
          {task.priority.charAt(0).toUpperCase() + task.priority.slice(1)} priority
        </span>
        <span>Updated {formatDate(task.updated_at)}</span>
      </div>
    </div>
  )
}

function TaskDetailPanel({
  task,
  ownerOptions,
  onUpdate,
  onDelete,
  comments,
  commentDrafts,
  setCommentDrafts,
  onAddComment,
  onClose
}) {
  const status = statusConfig(task.status)
  const overdue = isOverdue(task)
  const topLevelComments = comments.filter(comment => !comment.parent_comment_id)
  const repliesByParent = comments.reduce((acc, comment) => {
    if (!comment.parent_comment_id) return acc
    if (!acc[comment.parent_comment_id]) acc[comment.parent_comment_id] = []
    acc[comment.parent_comment_id].push(comment)
    return acc
  }, {})

  return (
    <div style={{
      position: 'fixed',
      inset: 0,
      background: 'rgba(15, 23, 42, 0.18)',
      zIndex: 1000,
      display: 'flex',
      justifyContent: 'flex-end'
    }}>
      <div style={{
        width: 460,
        maxWidth: '100%',
        height: '100%',
        background: '#FFFFFF',
        borderLeft: '1px solid var(--hv-border)',
        boxShadow: '-20px 0 60px rgba(15, 23, 42, 0.16)',
        padding: 24,
        overflowY: 'auto'
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 14, marginBottom: 20 }}>
          <div>
            <div style={{ fontSize: 12, fontWeight: 900, color: 'var(--hv-text-muted)', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 8 }}>
              Task
            </div>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              <span className="hv-badge hv-badge-gray" style={{ background: status.bg, color: status.color }}>
                {status.label}
              </span>
              <span className="hv-badge hv-badge-gray">
                {task.priority.charAt(0).toUpperCase() + task.priority.slice(1)}
              </span>
              {overdue && <span className="hv-badge hv-badge-red">Overdue</span>}
            </div>
          </div>

          <button className="hv-btn hv-btn-ghost" style={{ padding: 6 }} onClick={onClose}>
            <X size={17} />
          </button>
        </div>

        <textarea
          className="hv-input"
          value={task.title}
          onChange={event => onUpdate({ title: event.target.value })}
          style={{
            minHeight: 92,
            resize: 'vertical',
            fontSize: 18,
            fontWeight: 800,
            lineHeight: 1.35,
            marginBottom: 18
          }}
        />

        <div style={{ display: 'grid', gap: 12, marginBottom: 22 }}>
          <DetailField label="Status">
            <select
              className="hv-select"
              value={task.status}
              onChange={event => onUpdate({ status: event.target.value })}
              style={{ color: status.color, background: status.bg, fontWeight: 800 }}
            >
              {STATUS_OPTIONS.map(option => (
                <option key={option.value} value={option.value}>{option.label}</option>
              ))}
            </select>
          </DetailField>

          <DetailField label="Owner">
            <select
              className="hv-select"
              value={task.owner}
              onChange={event => onUpdate({ owner: event.target.value })}
            >
              <option value="">Unassigned</option>
              {task.owner && !ownerOptions.some(owner => getOwnerLabel(owner) === task.owner) && (
                <option value={task.owner}>{task.owner}</option>
              )}
              {ownerOptions.map(owner => {
                const label = getOwnerLabel(owner)

                return (
                  <option key={owner.id} value={label}>
                    {label}
                  </option>
                )
              })}
            </select>
          </DetailField>

          <DetailField label="Due Date">
            <input
              className="hv-input"
              type="date"
              value={task.due_date}
              onChange={event => onUpdate({ due_date: event.target.value })}
              style={{ borderColor: overdue ? '#FCA5A5' : 'var(--hv-border)' }}
            />
          </DetailField>

          <DetailField label="Priority">
            <select
              className="hv-select"
              value={task.priority}
              onChange={event => onUpdate({ priority: event.target.value })}
            >
              {PRIORITY_OPTIONS.map(option => (
                <option key={option.value} value={option.value}>{option.label}</option>
              ))}
            </select>
          </DetailField>
        </div>

        <div style={{ marginBottom: 22 }}>
          <label className="hv-form-label">Notes</label>
          <textarea
            className="hv-input"
            value={task.notes || ''}
            onChange={event => onUpdate({ notes: event.target.value })}
            placeholder="Add notes, decisions, blockers or links..."
            style={{ minHeight: 160, resize: 'vertical', lineHeight: 1.6 }}
          />
        </div>

        <div style={{ marginBottom: 22 }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, marginBottom: 10 }}>
            <label className="hv-form-label" style={{ marginBottom: 0 }}>
              Comments
            </label>
            <span className="hv-badge hv-badge-gray">
              {comments.length}
            </span>
          </div>

          <CommentComposer
            value={commentDrafts[task.id] || ''}
            onChange={value => setCommentDrafts(prev => ({ ...prev, [task.id]: value }))}
            onSubmit={() => onAddComment(task.id)}
            placeholder="Post an update or question..."
          />

          <div style={{ display: 'grid', gap: 12, marginTop: 14 }}>
            {topLevelComments.length === 0 ? (
              <div style={{
                border: '1px dashed var(--hv-border)',
                borderRadius: 10,
                padding: 16,
                color: 'var(--hv-text-muted)',
                fontSize: 12,
                textAlign: 'center'
              }}>
                No comments yet.
              </div>
            ) : topLevelComments.map(comment => (
              <CommentThread
                key={comment.id}
                comment={comment}
                replies={repliesByParent[comment.id] || []}
                draft={commentDrafts[comment.id] || ''}
                onDraftChange={value => setCommentDrafts(prev => ({ ...prev, [comment.id]: value }))}
                onReply={() => onAddComment(task.id, comment.id)}
              />
            ))}
          </div>
        </div>

        <div style={{
          display: 'grid',
          gap: 7,
          fontSize: 12,
          color: 'var(--hv-text-muted)',
          borderTop: '1px solid var(--hv-border-light)',
          paddingTop: 16,
          marginBottom: 18
        }}>
          <div>Created {formatDate(task.created_at)}</div>
          <div>Last modified {formatDate(task.updated_at)}</div>
        </div>

        <div style={{ display: 'flex', justifyContent: 'space-between', gap: 10 }}>
          <button
            className="hv-btn hv-btn-secondary"
            onClick={() => onUpdate({ status: task.status === 'done' ? 'todo' : 'done' })}
          >
            {task.status === 'done' ? 'Mark Open' : 'Mark Complete'}
          </button>

          <button className="hv-btn hv-btn-danger" onClick={onDelete}>
            Delete Task
          </button>
        </div>
      </div>
    </div>
  )
}

function TaskModal({ form, setForm, ownerOptions, onSave, onClose }) {
  return (
    <div style={{
      position: 'fixed',
      inset: 0,
      zIndex: 1001,
      background: 'rgba(15, 23, 42, 0.32)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: 24
    }}>
      <form
        className="hv-card"
        onSubmit={onSave}
        style={{
          width: 620,
          maxWidth: '100%',
          padding: 24,
          boxShadow: '0 24px 80px rgba(15, 23, 42, 0.22)'
        }}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 14, marginBottom: 20 }}>
          <div>
            <div style={{ fontSize: 18, fontWeight: 900, color: 'var(--hv-text-primary)' }}>
              Add Task
            </div>
          </div>

          <button type="button" className="hv-btn hv-btn-ghost" style={{ padding: 6 }} onClick={onClose}>
            <X size={17} />
          </button>
        </div>

        <div style={{ display: 'grid', gap: 14 }}>
          <div>
            <label className="hv-form-label">Task</label>
            <input
              className="hv-input"
              value={form.title}
              onChange={event => setForm({ ...form, title: event.target.value })}
              placeholder="Task name"
              autoFocus
            />
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: 12 }}>
            <div>
              <label className="hv-form-label">Status</label>
              <select
                className="hv-select"
                value={form.status}
                onChange={event => setForm({ ...form, status: event.target.value })}
              >
                {STATUS_OPTIONS.map(option => (
                  <option key={option.value} value={option.value}>{option.label}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="hv-form-label">Priority</label>
              <select
                className="hv-select"
                value={form.priority}
                onChange={event => setForm({ ...form, priority: event.target.value })}
              >
                {PRIORITY_OPTIONS.map(option => (
                  <option key={option.value} value={option.value}>{option.label}</option>
                ))}
              </select>
            </div>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: 12 }}>
            <div>
              <label className="hv-form-label">Owner</label>
              <select
                className="hv-select"
                value={form.owner}
                onChange={event => setForm({ ...form, owner: event.target.value })}
              >
                <option value="">Unassigned</option>
                {ownerOptions.map(owner => {
                  const label = getOwnerLabel(owner)

                  return (
                    <option key={owner.id} value={label}>
                      {label}
                    </option>
                  )
                })}
              </select>
            </div>

            <div>
              <label className="hv-form-label">Due Date</label>
              <input
                className="hv-input"
                type="date"
                value={form.due_date}
                onChange={event => setForm({ ...form, due_date: event.target.value })}
              />
            </div>
          </div>

          <div>
            <label className="hv-form-label">Notes</label>
            <textarea
              className="hv-input"
              value={form.notes}
              onChange={event => setForm({ ...form, notes: event.target.value })}
              placeholder="Add context, links, decisions or blockers..."
              style={{ minHeight: 120, resize: 'vertical', lineHeight: 1.6 }}
            />
          </div>
        </div>

        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 20 }}>
          <button type="button" className="hv-btn hv-btn-secondary" onClick={onClose}>
            Cancel
          </button>
          <button type="submit" className="hv-btn hv-btn-primary">
            Add Task
          </button>
        </div>
      </form>
    </div>
  )
}

function CommentComposer({ value, onChange, onSubmit, placeholder }) {
  return (
    <div style={{ display: 'grid', gap: 8 }}>
      <textarea
        className="hv-input"
        value={value}
        onChange={event => onChange(event.target.value)}
        placeholder={placeholder}
        style={{ minHeight: 72, resize: 'vertical', lineHeight: 1.5 }}
      />
      <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
        <button
          className="hv-btn hv-btn-primary"
          disabled={!value.trim()}
          onClick={onSubmit}
        >
          <Send size={14} />
          Post
        </button>
      </div>
    </div>
  )
}

function CommentThread({ comment, replies, draft, onDraftChange, onReply }) {
  const author = comment.profiles?.full_name || comment.profiles?.email || 'Unknown user'

  return (
    <div style={{
      border: '1px solid var(--hv-border-light)',
      borderRadius: 10,
      padding: 12,
      background: '#FFFFFF'
    }}>
      <CommentBubble comment={comment} author={author} />

      {replies.length > 0 && (
        <div style={{ display: 'grid', gap: 10, marginTop: 12, paddingLeft: 16, borderLeft: '2px solid var(--hv-border-light)' }}>
          {replies.map(reply => (
            <CommentBubble
              key={reply.id}
              comment={reply}
              author={reply.profiles?.full_name || reply.profiles?.email || 'Unknown user'}
            />
          ))}
        </div>
      )}

      <div style={{ marginTop: 12, paddingLeft: replies.length > 0 ? 16 : 0 }}>
        <CommentComposer
          value={draft}
          onChange={onDraftChange}
          onSubmit={onReply}
          placeholder="Reply..."
        />
      </div>
    </div>
  )
}

function CommentBubble({ comment, author }) {
  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
        <div style={{
          width: 26,
          height: 26,
          borderRadius: '50%',
          background: '#EEF2FF',
          color: '#4F46E5',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: 10,
          fontWeight: 900
        }}>
          {author.split(/[\s._-]+/).slice(0, 2).map(part => part[0]?.toUpperCase()).join('')}
        </div>
        <div>
          <div style={{ fontSize: 12, fontWeight: 900, color: 'var(--hv-text-primary)' }}>{author}</div>
          <div style={{ fontSize: 11, color: 'var(--hv-text-muted)' }}>{formatDate(comment.created_at)}</div>
        </div>
      </div>
      <div style={{ fontSize: 12.5, color: 'var(--hv-text-secondary)', lineHeight: 1.55, whiteSpace: 'pre-wrap' }}>
        {comment.body}
      </div>
    </div>
  )
}

function DetailField({ label, children }) {
  return (
    <div style={{
      display: 'grid',
      gridTemplateColumns: '110px minmax(0, 1fr)',
      gap: 12,
      alignItems: 'center'
    }}>
      <div style={{ fontSize: 12, fontWeight: 800, color: 'var(--hv-text-muted)' }}>
        {label}
      </div>
      {children}
    </div>
  )
}
